export type EcolabelColumnKey =
  | "name"
  | "plant_ecolabel_id"
  | "standard"
  | "status"
  | "country"
  | "address"
  | "latitude"
  | "longitude"
  | "renewable_type"
  | "capacity"
  | "cod"
  | "date_reviewed"
  | "valid_from"
  | "valid_through"
  | "registration_authority"
  | "technology_code"
  | "public_notes"
  | "relevant_link"
  | "documents";

export interface EcolabelColumnDefinition {
  field: EcolabelColumnKey;
  header: string;
  width: { compact: string; default: string };
}

type EcolabelColumnProperties = Omit<EcolabelColumnDefinition, "field">;

const ECOLABEL_COLUMN_DEFINITIONS: Record<
  EcolabelColumnKey,
  EcolabelColumnProperties
> = {
  name: {
    header: "ecolabels.table.name",
    // Wide enough for full plant names (customer-reported truncation).
    width: { compact: "12rem", default: "18rem" },
  },
  plant_ecolabel_id: {
    header: "ecolabels.table.plant_ecolabel_id",
    width: { compact: "7rem", default: "9rem" },
  },
  standard: {
    header: "ecolabels.table.standard",
    width: { compact: "7rem", default: "9rem" },
  },
  status: {
    header: "ecolabels.table.status",
    width: { compact: "6.5rem", default: "7.5rem" },
  },
  country: {
    header: "ecolabels.table.country",
    width: { compact: "5rem", default: "6rem" },
  },
  address: {
    header: "ecolabels.table.address",
    width: { compact: "9rem", default: "12rem" },
  },
  latitude: {
    header: "ecolabels.table.latitude",
    width: { compact: "5.5rem", default: "6.5rem" },
  },
  longitude: {
    header: "ecolabels.table.longitude",
    width: { compact: "5.5rem", default: "6.5rem" },
  },
  renewable_type: {
    header: "ecolabels.table.renewable_type",
    width: { compact: "6rem", default: "7.5rem" },
  },
  technology_code: {
    header: "ecolabels.table.technology_code",
    width: { compact: "6rem", default: "7rem" },
  },
  capacity: {
    header: "ecolabels.table.capacity",
    width: { compact: "5.5rem", default: "6.5rem" },
  },
  cod: {
    header: "ecolabels.table.cod",
    width: { compact: "5.5rem", default: "7rem" },
  },
  date_reviewed: {
    header: "ecolabels.table.date_reviewed",
    width: { compact: "6rem", default: "7.5rem" },
  },
  valid_from: {
    header: "ecolabels.table.valid_from",
    width: { compact: "6rem", default: "7.5rem" },
  },
  valid_through: {
    header: "ecolabels.table.valid_through",
    width: { compact: "6rem", default: "7.5rem" },
  },
  registration_authority: {
    header: "ecolabels.table.registration_authority",
    width: { compact: "7rem", default: "9rem" },
  },
  public_notes: {
    header: "ecolabels.table.public_notes",
    width: { compact: "7rem", default: "10rem" },
  },
  relevant_link: {
    header: "ecolabels.table.relevant_link",
    width: { compact: "6rem", default: "8rem" },
  },
  documents: {
    header: "ecolabels.table.documents",
    width: { compact: "5rem", default: "6rem" },
  },
};

export const ALL_ECOLABEL_COLUMNS = Object.fromEntries(
  Object.entries(ECOLABEL_COLUMN_DEFINITIONS).map(([key, props]) => [
    key,
    { field: key as EcolabelColumnKey, ...props },
  ])
) as Record<EcolabelColumnKey, EcolabelColumnDefinition>;

export const DEFAULT_ECOLABEL_COLUMNS: EcolabelColumnKey[] = [
  "name",
  "plant_ecolabel_id",
  "standard",
  "status",
  "country",
  "latitude",
  "longitude",
  "renewable_type",
  "capacity",
  "cod",
  "date_reviewed",
  "valid_from",
  "valid_through",
  "public_notes",
  "relevant_link",
  "documents",
];
